/**
 * NarratorFloatingPill
 *
 * Compact floating pill indicator that appears when the narrator card
 * is scrolled off-screen and its text updates during a rewrite session.
 *
 * Behavior:
 * - Shows only when narrator card is off-screen AND text has changed
 * - Does NOT show during initial analysis load
 * - Slides down from top with spring animation
 * - Auto-dismisses after 4 seconds
 * - Tap scrolls to narrator card, then dismisses
 * - New updates replace existing pill (reset dismiss timer)
 * - Haptic feedback on appear (mobile only)
 *
 * Positioning:
 * - Web: position 'fixed' so it stays visible above ScrollView content
 * - Native: position 'absolute' (rendered outside scroll context by parent)
 *
 * Design: Semi-transparent glass pill matching Y2K + glassmorphism aesthetic
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { Text, Pressable, StyleSheet, Platform } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { triggerSelection } from '@/lib/haptics';
import {
  captureNarratorPillShown,
  captureNarratorPillTapped,
} from '@/lib/analytics/events/narrator-events';

/** Maximum characters to show in the pill before truncating */
const MAX_TEXT_LENGTH = 40;

/** Auto-dismiss delay in milliseconds */
const AUTO_DISMISS_MS = 4000;

/** Spring config for slide-in animation */
const SPRING_CONFIG = {
  damping: 18,
  stiffness: 200,
  mass: 0.8,
};

interface NarratorFloatingPillProps {
  /** Current narrator reaction text */
  text: string;
  /** Whether the narrator card is visible in viewport */
  isCardVisible: boolean;
  /** Whether we're in an active rewrite session (not initial load) */
  isSessionActive: boolean;
  /** Callback to scroll to narrator card */
  onTap: () => void;
  /** What triggered the current reaction (for analytics) */
  trigger?: string;
  /** User subscription tier (for analytics) */
  tier?: string;
  /** Test ID for testing */
  testID?: string;
}

/**
 * Truncate text to maxLen characters with ellipsis.
 */
function truncateText(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + '\u2026';
}

export function NarratorFloatingPill({
  text,
  isCardVisible,
  isSessionActive,
  onTap,
  trigger = 'unknown',
  tier = 'free',
  testID = 'narrator-floating-pill',
}: NarratorFloatingPillProps) {
  const insets = useSafeAreaInsets();

  // Animation values
  const translateY = useSharedValue(-60);
  const opacity = useSharedValue(0);

  // Track pill visibility as React state so pointerEvents updates correctly
  const [isPillVisible, setIsPillVisible] = useState(false);

  // Track previous text to detect changes
  const prevTextRef = useRef(text);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Analytics tracking refs
  const pillShownTimeRef = useRef<number>(0);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearDismissTimer();
    setIsPillVisible(false);
    opacity.value = withTiming(0, { duration: 200 });
    translateY.value = withTiming(-60, { duration: 200 });
  }, [clearDismissTimer, opacity, translateY]);

  // Keep refs so show/handleTap can read current values without stale closure issues
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;
  const tierRef = useRef(tier);
  tierRef.current = tier;
  const textRef = useRef(text);
  textRef.current = text;

  const show = useCallback(() => {
    clearDismissTimer();
    setIsPillVisible(true);

    // Slide down from top with spring
    translateY.value = withSpring(0, SPRING_CONFIG);
    opacity.value = withTiming(1, { duration: 200 });

    // Haptic on appear
    triggerSelection();

    // Record time pill became visible for tap latency tracking
    pillShownTimeRef.current = Date.now();

    // Fire analytics
    captureNarratorPillShown({
      trigger: triggerRef.current,
      tier: tierRef.current,
      reaction_preview: truncateText(textRef.current, MAX_TEXT_LENGTH),
    });

    // Auto-dismiss after 4s
    dismissTimerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
  }, [clearDismissTimer, translateY, opacity, dismiss]);

  // React to text changes + visibility conditions
  useEffect(() => {
    const textChanged = text !== prevTextRef.current;
    prevTextRef.current = text;

    const shouldShow = isSessionActive && !isCardVisible && textChanged && text.length > 0;

    if (shouldShow) {
      show();
    } else if (isCardVisible && isPillVisible) {
      // Card scrolled back into view - dismiss pill
      dismiss();
    }
  }, [text, isCardVisible, isSessionActive, isPillVisible, show, dismiss]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => clearDismissTimer();
  }, [clearDismissTimer]);

  const handleTap = useCallback(() => {
    triggerSelection();

    // Fire analytics before dismiss clears state
    captureNarratorPillTapped({
      trigger: triggerRef.current,
      tier: tierRef.current,
      time_since_pill_shown_ms: pillShownTimeRef.current > 0
        ? Date.now() - pillShownTimeRef.current
        : 0,
    });

    onTap();
    dismiss();
  }, [onTap, dismiss]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  const truncated = truncateText(text, MAX_TEXT_LENGTH);

  return (
    <Animated.View
      style={[
        styles.wrapper,
        { top: insets.top + 8 },
        animatedStyle,
      ]}
      pointerEvents={isPillVisible ? 'auto' : 'none'}
      testID={testID}
    >
      <Pressable
        onPress={handleTap}
        style={styles.pill}
        accessibilityRole="button"
        accessibilityLabel={`Narrator update: ${truncated}. Tap to scroll to narrator.`}
        testID={`${testID}-pressable`}
      >
        <Text style={styles.emoji} accessibilityElementsHidden>
          {'\uD83D\uDCAC'}
        </Text>
        <Text style={styles.text} numberOfLines={1}>
          {truncated}
        </Text>
        <Text style={styles.arrow} accessibilityElementsHidden>
          {'\u2191'}
        </Text>
      </Pressable>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    // Web: 'fixed' keeps pill visible above ScrollView content
    // Native: 'absolute' -- parent must render this outside scroll context
    position: Platform.OS === 'web' ? ('fixed' as 'absolute') : 'absolute',
    left: 16,
    right: 16,
    zIndex: 50,
    alignItems: 'center',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 18,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    gap: 6,
    maxWidth: 400,
    width: '100%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
      web: {
        backdropFilter: 'blur(12px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
      },
    }),
  },
  emoji: {
    fontSize: 14,
  },
  text: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18,
  },
  arrow: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
    fontWeight: '600',
  },
});
